import EmptyAssetCard from "common/features/asset/EmptyAssetCard";
import styles from "./Material.module.scss";
import MaterialMetaData from "./MaterialMetaData";
import StatusGarnish from "./StatusGarnish";
import { useCallback, useEffect, useRef, useState } from "react";
import MaterialPanel from "./MaterialPanel";
import useMaterialActions from "./useMaterialActions";
import MaterialActions from "./MaterialActions";
import AccessCodeDialog from "./AccessCodeDialog";
import ConfirmOptOutItemDialog from "./ConfirmOptOutItemDialog";
import ProgramSwitcher from "./ProgramSwitcher";
import { CourseMaterial, useCoursesLoaderData } from "./coursesLoader";
import { useNavigate } from "react-router-dom";
import PrintStatusTag from "./PrintStatusTag";
import clsx from "clsx";
import ConfirmOptInPrintDialog from "./ConfirmOptInPrintDialog";
import { ConfirmOptInEvent, PrintOptInEvent } from "./PrintStatusChangeToast";
import LmsLinkDialog from "./LmsLinkDialog";
import RequiredCodeTag from "../common/RequiredCodeTag.tsx";
import { FormattedMessage } from "react-intl";
import useVirtualStore from "./useVirtualStore.tsx";
import useProgramDecision from "./useProgramDecision.tsx";
import { getImageSource } from "./materialUtils.ts";
import useLaunchBook from "./useLaunchBook.tsx";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";
import useInstitutionProgram from "./useInstitutionProgram.tsx";
import MaterialPrice from "./MaterialPrice.tsx";
import StoreStockStatusTag from "./StoreStockStatusTag.tsx";

export default function Material({
  courseMaterial,
}: {
  courseMaterial: CourseMaterial;
}) {
  const { currentTerm } = useCoursesLoaderData();
  const { vbid, title, displayNotes, courseId, printStatus, materialStatus } =
    courseMaterial;
  const [openPanelInfo, setOpenPanelInfo] = useState<boolean>(false);
  const [isProgramSwitcherOpen, setIsProgramSwitcherOpen] =
    useState<boolean>(false);
  const [openConfirmOptOutDialog, setOpenConfirmOptOutDialog] =
    useState<boolean>(false);
  const [openConfirmOptInPrintDialog, setOpenConfirmOptInPrintDialog] =
    useState<boolean>(false);
  const [accessCodeDialogUrl, setAccessCodeDialogUrl] = useState("");
  const [lmsLinkDialogUrl, setLmsLinkDialogUrl] = useState("");
  const { launchBook } = useLaunchBook();
  const navigate = useNavigate();
  const pricingRef = useRef<HTMLDivElement>(null);
  const { optWindowOpen, optDeadline } = useMaterialOptDeadline(courseMaterial);
  const decision = useProgramDecision();
  const { isVirtualStore } = useVirtualStore();
  const showEAReminder =
    isVirtualStore &&
    currentTerm?.institutionProgram === "equitable_access" &&
    decision === "no_program" &&
    optWindowOpen &&
    optDeadline;
  const { program } = useInstitutionProgram();
  const selectProgram =
    currentTerm?.institutionProgram === "flexible_access"
      ? () => setIsProgramSwitcherOpen(true)
      : undefined;

  const methods = {
    comparePurchaseOptions: () =>
      navigate(
        `/my-courses/purchase-options/${currentTerm!.id}/${courseId}/${vbid}`
      ),
    confirmOptOutDialog: () => setOpenConfirmOptOutDialog(true),
    confirmOptInPrintDialog: () => setOpenConfirmOptInPrintDialog(true),
    viewDetails: () => setOpenPanelInfo(true),
    launchBook: (path: string) => launchBook(path),
    accessCodeDialog: (url: string) => setAccessCodeDialogUrl(url),
    lmsLinkDialog: (url: string) => setLmsLinkDialogUrl(url),
  };
  const actions = useMaterialActions({
    courseMaterial,
    methods,
  });

  const handlePrintOptIn = useCallback(
    (event: CustomEvent<ConfirmOptInEvent>) => {
      if (event?.detail?.courseMaterialId === courseMaterial.id) {
        const action = actions.find((action) => action.type === "opt_in");
        if (action?.onClick) {
          void action.onClick();
        }
      }
    },
    [actions, courseMaterial.id]
  );

  const getStatusGarnish = () => {
    if (optWindowOpen) {
      if (program === "inclusive_access" && materialStatus === "not_included") {
        return null;
      }
      return <StatusGarnish materialStatus={courseMaterial.materialStatus} />;
    }
    return null;
  };

  useEffect(() => {
    window.addEventListener(PrintOptInEvent, handlePrintOptIn);

    return () => {
      window.removeEventListener(PrintOptInEvent, handlePrintOptIn);
    };
  }, [handlePrintOptIn]);

  return (
    <li className={styles.wholeCard}>
      <div className={styles.cardContainer}>
        <div>
          <EmptyAssetCard
            tagName="div"
            vbid={vbid}
            type="horizontal"
            imageGarnish={getStatusGarnish()}
            imageSource={getImageSource(courseMaterial)}
            contentAreaClassName={styles.contentContainer}
            className={styles.baseCard}
            imageAddendum={
              courseMaterial.requiredCode && (
                <div
                  className={styles.requiredCodeTagContainer}
                  data-testid="requiredCode"
                >
                  <RequiredCodeTag code={courseMaterial.requiredCode} />
                </div>
              )
            }
          >
            <div className={styles.metaDataContainer}>
              <div
                className={clsx(styles.statusSmall, styles.statusTagContainer)}
              >
                <PrintStatusTag printStatus={printStatus} />
                <StoreStockStatusTag stockItem={courseMaterial} />
              </div>
              <MaterialMetaData
                courseMaterial={courseMaterial}
                titleTag={"h3"}
              />
              <MaterialPrice ref={pricingRef} material={courseMaterial} />
            </div>
          </EmptyAssetCard>
          {showEAReminder && (
            <div className={styles.supplementalMessage}>
              <FormattedMessage
                defaultMessage="This item is included in Equitable Access. You can opt back into the program until {date, date, ::yyyyMMMMdd} at {time, time, short}."
                values={{
                  date: new Date(optDeadline),
                  time: new Date(optDeadline),
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.actionsContainer}>
          <div className={clsx(styles.statusLarge, styles.statusTagContainer)}>
            <PrintStatusTag printStatus={printStatus} />
            <StoreStockStatusTag stockItem={courseMaterial} />
          </div>
          <MaterialActions
            material={courseMaterial}
            className={clsx(
              styles.actions,
              isVirtualStore && styles.wideActions
            )}
            actions={actions}
            pricingRef={pricingRef}
          />
        </div>
      </div>
      {openPanelInfo && (
        <MaterialPanel
          courseMaterial={courseMaterial}
          actions={actions}
          setOpenPanelInfo={() => setOpenPanelInfo(false)}
        />
      )}
      {accessCodeDialogUrl && (
        <AccessCodeDialog
          title={title}
          displayNotes={displayNotes}
          vbid={vbid}
          onRequestClose={() => setAccessCodeDialogUrl("")}
          fetchUrl={accessCodeDialogUrl}
        />
      )}
      {lmsLinkDialogUrl && (
        <LmsLinkDialog
          onRequestClose={() => setLmsLinkDialogUrl("")}
          materialFormat={courseMaterial.materialFormat}
          href={lmsLinkDialogUrl}
        />
      )}
      {openConfirmOptOutDialog && (
        <ConfirmOptOutItemDialog
          courseMaterial={courseMaterial}
          selectProgram={selectProgram}
          setOpenConfirmOptOutDialog={() => setOpenConfirmOptOutDialog(false)}
        />
      )}
      {openConfirmOptInPrintDialog && (
        <ConfirmOptInPrintDialog
          courseMaterial={courseMaterial}
          onRequestClose={() => setOpenConfirmOptInPrintDialog(false)}
        />
      )}
      {currentTerm?.institutionProgram === "flexible_access" &&
        isProgramSwitcherOpen && (
          <ProgramSwitcher
            onRequestClose={() => setIsProgramSwitcherOpen(false)}
          />
        )}
    </li>
  );
}
